import { useRef, useState } from "react";
import { FileArchive, FolderInput, Files, Upload } from "lucide-react";

/**
 * MultiSourceDropzone — universal file intake used by Knuth + Kailash.
 * Users can:
 *   - drag and drop many files at once
 *   - drag a folder onto the dropzone (browser dependent — Chromium/Safari OK)
 *   - pick multiple files via file dialog
 *   - pick an entire folder via folder-picker dialog (webkitdirectory)
 *   - drop one or more ZIP archives
 * Everything is coalesced into a single `File[]` and pushed to the parent
 * via `onFiles`.
 */
export default function MultiSourceDropzone({ files, onFiles, accept, "data-testid": testid = "multi-dropzone" }) {
  const filesInput = useRef(null);
  const folderInput = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const collect = async (dt) => {
    // Prefer the item interface — it exposes folders in Chromium/Safari
    const items = dt?.items ? Array.from(dt.items) : [];
    const collected = [];
    if (items.length && items.some((i) => typeof i.webkitGetAsEntry === "function")) {
      const walkers = items
        .map((i) => (typeof i.webkitGetAsEntry === "function" ? i.webkitGetAsEntry() : null))
        .filter(Boolean)
        .map((entry) => walk(entry, "", collected));
      await Promise.all(walkers);
    }
    if (!collected.length) {
      // Fallback to the flat file list
      Array.from(dt?.files || []).forEach((f) => collected.push(f));
    }
    return collected;
  };

  const walk = (entry, prefix, out) => new Promise((resolve) => {
    if (entry.isFile) {
      entry.file((file) => {
        // Preserve the relative path so ZIP extraction downstream stays sensible
        try { Object.defineProperty(file, "relativePath", { value: prefix + entry.name }); } catch { /* ignore */ }
        out.push(file);
        resolve();
      }, () => resolve());
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const readAll = () => reader.readEntries(async (entries) => {
        if (!entries.length) { resolve(); return; }
        await Promise.all(entries.map((e) => walk(e, prefix + entry.name + "/", out)));
        readAll();
      }, () => resolve());
      readAll();
    } else { resolve(); }
  });

  const onDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const gathered = await collect(e.dataTransfer);
    if (gathered.length) onFiles([...(files || []), ...gathered]);
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) onFiles([...(files || []), ...picked]);
    e.target.value = "";
  };

  const onPickFolder = (e) => {
    const picked = Array.from(e.target.files || []);
    // Chromium sets webkitRelativePath — keep it so callers can group
    if (picked.length) onFiles([...(files || []), ...picked]);
    e.target.value = "";
  };

  const clear = () => onFiles([]);
  const removeOne = (idx) => onFiles((files || []).filter((_, i) => i !== idx));

  const totalKb = (files || []).reduce((s, f) => s + f.size, 0) / 1024;
  const zipCount = (files || []).filter((f) => (f.name || "").toLowerCase().endsWith(".zip")).length;

  return (
    <div className="space-y-3" data-testid={testid}>
      <div
        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors cursor-pointer ${dragOver ? "border-marigold bg-marigold/5" : "border-border hover:border-ink/40"}`}
        onClick={() => filesInput.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid={`${testid}-surface`}
      >
        <FileArchive className="w-8 h-8 mx-auto mb-3 text-ink2" />
        <p className="text-sm text-ink mb-1 font-medium">Drop files, folders, or ZIP archives here</p>
        <p className="text-xs text-ink2/70">Multi-select supported · folders unpack recursively · up to 25MB total · secrets skipped</p>
        <div className="mt-4 inline-flex gap-2">
          <button
            type="button"
            className="btn-outline-ink text-xs py-1.5"
            onClick={(e) => { e.stopPropagation(); filesInput.current?.click(); }}
            data-testid={`${testid}-pick-files`}
          >
            <Files className="w-3.5 h-3.5" /> Choose files
          </button>
          <button
            type="button"
            className="btn-outline-ink text-xs py-1.5"
            onClick={(e) => { e.stopPropagation(); folderInput.current?.click(); }}
            data-testid={`${testid}-pick-folder`}
          >
            <FolderInput className="w-3.5 h-3.5" /> Choose folder
          </button>
        </div>
        <input ref={filesInput} type="file" multiple accept={accept} className="hidden" onChange={onPickFiles} />
        <input
          ref={folderInput} type="file"
          {...{ webkitdirectory: "", directory: "" }}
          className="hidden"
          onChange={onPickFolder}
        />
      </div>

      {files && files.length > 0 && (
        <div className="rounded-xl border border-border bg-cream p-3" data-testid={`${testid}-list`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-ink2 inline-flex items-center gap-2">
              <Upload className="w-3.5 h-3.5" /> {files.length} file{files.length === 1 ? "" : "s"} · {totalKb.toFixed(1)} KB
              {zipCount > 0 && <span className="text-marigold">· {zipCount} ZIP{zipCount === 1 ? "" : "s"}</span>}
            </div>
            <button type="button" className="text-xs text-ink2 hover:text-marigold" onClick={clear}>Clear</button>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1 text-xs font-mono">
            {files.slice(0, 40).map((f, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-ink2">
                <span className="truncate">{f.webkitRelativePath || f.relativePath || f.name}</span>
                <button type="button" onClick={() => removeOne(i)} className="text-ink2 hover:text-marigold flex-shrink-0">×</button>
              </div>
            ))}
            {files.length > 40 && <div className="text-ink2/60">…and {files.length - 40} more</div>}
          </div>
        </div>
      )}
    </div>
  );
}
